import { FC, useCallback, useEffect, useState } from 'react';
import style from './unit-builder-modal.module.scss';

interface UnitBuilderRowResizerProps {
  /**
   * CSS custom property this handle drives, set on the pane element. The rule
   * that consumes it keeps the design default as its fallback, so the handle
   * only ever overrides a working layout.
   */
  cssVar: string;
  /** Floor, in px. */
  min: number;
  /**
   * Optional hard ceiling, in px. Normally omitted: the ceiling is derived from
   * the space actually below the handle, so it follows the window instead of
   * being a number that happens to suit one screen.
   */
  max?: number;
  label: string;
}

/**
 * Space kept free under the handle, so the section it shares the pane with (the
 * survey rows, the slots table) can never be dragged away to nothing.
 */
const MIN_BELOW_PX = 160;

/**
 * Horizontal drag handle that resizes the block above it.
 *
 * The mock gives the map and the gun barrel chart their own splitter, so an
 * operator can trade map height against the survey rows, or chart height
 * against the slots table, without the shell relaying out. This is the vertical
 * counterpart of the shell's column divider and works the same way: it writes a
 * custom property on the pane rather than owning the height in React state, so
 * the CSS keeps its own default and nothing re-renders per mouse move.
 *
 * @param {UnitBuilderRowResizerProps} props - The property to drive and its clamp.
 * @return {JSX.Element} The handle.
 */
export const UnitBuilderRowResizer: FC<UnitBuilderRowResizerProps> = ({
  cssVar,
  min,
  max,
  label,
}) => {
  const [dragging, setDragging] = useState(false);

  const onMouseDown = useCallback(() => setDragging(true), []);

  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (event: MouseEvent) => {
      // Measure from the resized element itself, found by the same property
      // name. Using the handle's previous sibling looked simpler but is wrong
      // here: on the map pane that sibling wraps the toolbar as well as the
      // canvas, so the origin sat a toolbar's height too high and every drag
      // overshot.
      const handle = document.querySelector<HTMLElement>(
        `[data-resizer="${cssVar}"]`
      );
      const target = document.querySelector<HTMLElement>(
        `[data-resize-target="${cssVar}"]`
      );
      if (!target) return;

      const top = target.getBoundingClientRect().top;
      // Derived, not fixed. A hardcoded ceiling (720/760px) stopped the drag
      // well short of the available room on a tall window -- and browser zoom
      // makes this worse, since zooming out grows the CSS viewport while the
      // pixel ceiling stays put, which is why 60-70% zoom felt broken.
      // The viewport bottom is the stable reference: the sheet is bottom
      // anchored, so this cannot recede as the pane grows.
      const ceiling = window.innerHeight - top - MIN_BELOW_PX;
      const limit = Math.max(min, Math.min(max ?? Infinity, ceiling));
      const next = Math.min(limit, Math.max(min, event.clientY - top));
      const pane = handle?.closest<HTMLElement>('[data-unit-builder-pane]');
      (pane ?? document.documentElement).style.setProperty(cssVar, `${next}px`);
    };

    const onUp = () => setDragging(false);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    // Dragging over the map would otherwise start a Mapbox pan and select text.
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = previousUserSelect;
    };
  }, [dragging, cssVar, min, max]);

  return (
    <div
      className={style.rowResizer}
      data-resizer={cssVar}
      role="separator"
      aria-orientation="horizontal"
      aria-label={label}
      onMouseDown={onMouseDown}
    >
      <span className={style.rowResizerGrip} aria-hidden>
        ⠿
      </span>
    </div>
  );
};
